import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plane, Train, Car, Ship, Bus, HelpCircle, ArrowLeftRight, Camera, Check, ChevronLeft, ChevronRight } from "lucide-react";
import CityAutocomplete from "@/components/travel/CityAutocomplete";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";

const MODES = [
  { k: "Avion", Ic: Plane }, { k: "Train", Ic: Train }, { k: "Route", Ic: Car },
  { k: "Bateau", Ic: Ship }, { k: "Bus", Ic: Bus }, { k: "Autre", Ic: HelpCircle },
];

const EXCLUSIONS_OPTS = ["Liquides","Objets fragiles","Nourriture","Électronique de valeur","Documents officiels","Armes/dangereux","Vivants (animaux, plantes)"];

export default function TripWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    mode: null, origin: { label: "" }, destination: { label: "" },
    depart_at: "", arrive_at: "", ticket_photo_url: "",
    availability: "", exclusions: [], hide_contact: true, is_pro: false,
    freight: {},
  });
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    return (
      <div className="max-w-md mx-auto p-8 text-center">
        <p className="text-slate-300 mb-4">Connectez-vous pour publier un trajet.</p>
        <Link to="/login" data-testid="wizard-login-cta" className="btn-coral px-4 py-2 rounded-lg text-sm">Se connecter</Link>
      </div>
    );
  }

  const canNext = () => {
    if (step === 1) return !!data.mode;
    if (step === 2) return data.origin.label && data.destination.label;
    if (step === 3) {
      if (!data.depart_at || !data.arrive_at) return false;
      return new Date(data.arrive_at) > new Date(data.depart_at);
    }
    if (step === 4) return data.mode === "Route" || !!data.ticket_photo_url || true; // photo optional per V1
    if (step === 5) return true;
    return true;
  };

  const swap = () => setData((d) => ({ ...d, origin: d.destination, destination: d.origin }));

  const toggleExcl = (v) => setData((d) => ({ ...d, exclusions: d.exclusions.includes(v) ? d.exclusions.filter(x=>x!==v) : [...d.exclusions, v] }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/trips", {
        mode: data.mode,
        origin: data.origin,
        destination: data.destination,
        depart_at: new Date(data.depart_at).toISOString(),
        arrive_at: new Date(data.arrive_at).toISOString(),
        ticket_photo_url: data.ticket_photo_url || null,
        availability: data.availability,
        exclusions: data.exclusions,
        hide_contact: data.hide_contact,
        is_pro: data.is_pro,
        freight: data.is_pro && data.freight ? data.freight : null,
      });
      toast.success("Trajet publié !");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {[1,2,3,4,5,6].map((n) => (
          <div key={n} className={`h-1.5 flex-1 rounded-full ${n<=step ? "bg-gradient-to-r from-[#FF6B6B] to-[#F59E0B]" : "bg-slate-800"}`} />
        ))}
      </div>
      <p className="text-xs text-slate-400 mb-4">Étape {step} / 6</p>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}
          className="glass-strong rounded-2xl p-6"
        >
          {step === 1 && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-2">Mode de transport</h2>
              <p className="text-sm text-slate-400 mb-5">Comment voyagez-vous ?</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {MODES.map(({k, Ic}) => (
                  <button key={k} data-testid={`wiz-mode-${k}`} onClick={()=>setData(d=>({...d, mode:k}))}
                    className={`p-4 rounded-xl border transition ${data.mode===k ? "border-[#FF6B6B] bg-slate-900/80" : "border-slate-800 bg-slate-900/40 hover:border-slate-600"}`}>
                    <Ic size={24} className={data.mode===k ? "text-[#FF6B6B]" : "text-slate-400"} />
                    <div className="mt-2 text-sm">{k}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-2">Lieux du trajet</h2>
              <p className="text-sm text-slate-400 mb-5">De quelle ville à quelle ville ?</p>
              <div className="space-y-3">
                <CityAutocomplete testid="wiz-origin" value={data.origin.label} onChange={(v)=>setData(d=>({...d, origin:v}))} placeholder="Point de départ précis" />
                <div className="flex justify-center">
                  <button type="button" data-testid="wiz-swap" onClick={swap} className="btn-ghost-glass p-2 rounded-full"><ArrowLeftRight size={16}/></button>
                </div>
                <CityAutocomplete testid="wiz-destination" value={data.destination.label} onChange={(v)=>setData(d=>({...d, destination:v}))} placeholder="Point d'arrivée précis" />
              </div>
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-2">Dates & horaires</h2>
              <p className="text-sm text-slate-400 mb-5">L'arrivée doit être après le départ.</p>
              <label className="text-xs text-slate-400">Départ</label>
              <input data-testid="wiz-depart" type="datetime-local" value={data.depart_at} onChange={(e)=>setData(d=>({...d, depart_at:e.target.value}))}
                className="w-full mt-1 mb-4 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 outline-none focus:border-[#FF6B6B] text-sm" />
              <label className="text-xs text-slate-400">Arrivée</label>
              <input data-testid="wiz-arrive" type="datetime-local" value={data.arrive_at} onChange={(e)=>setData(d=>({...d, arrive_at:e.target.value}))}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 outline-none focus:border-[#FF6B6B] text-sm" />
              {data.depart_at && data.arrive_at && new Date(data.arrive_at) <= new Date(data.depart_at) && (
                <p className="text-xs text-red-400 mt-2">L'arrivée doit être postérieure au départ.</p>
              )}
            </div>
          )}
          {step === 4 && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-2">Photo du billet</h2>
              <p className="text-sm text-slate-400 mb-5">Renforce la confiance des buddies. {data.mode === "Route" && "(non requis en mode Route)"}</p>
              <div className="flex items-center gap-2">
                <input data-testid="wiz-ticket" value={data.ticket_photo_url} onChange={(e)=>setData(d=>({...d, ticket_photo_url:e.target.value}))} placeholder="URL de la photo (ou laissez vide)"
                  className="flex-1 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 outline-none focus:border-[#FF6B6B] text-sm" />
                <span className="text-slate-500"><Camera size={18}/></span>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Upload direct disponible en V2.</p>
            </div>
          )}
          {step === 5 && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-2">Disponibilités & exclusions</h2>
              <p className="text-sm text-slate-400 mb-4">Précisez ce que vous acceptez de transporter.</p>
              <label className="text-xs text-slate-400 flex items-center justify-between">
                Disponibilités (créneaux, remise/récup)
                <button type="button" data-testid="wiz-ai-suggest" onClick={async ()=>{
                  if (!data.mode || !data.origin.label || !data.destination.label || !data.depart_at || !data.arrive_at) { toast.error("Complétez d'abord mode/lieux/dates"); return; }
                  try {
                    toast.info("Génération…");
                    const r = await api.post("/ai/suggest-availability", { mode: data.mode, origin: data.origin.label, destination: data.destination.label, depart_at: data.depart_at, arrive_at: data.arrive_at });
                    setData(d=>({...d, availability: r.data.text || d.availability }));
                    toast.success("Suggestion générée");
                  } catch { toast.error("AI indisponible"); }
                }} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B] border border-[#FF6B6B]/30 hover:bg-[#FF6B6B]/20">
                  ✨ AI Suggest
                </button>
              </label>
              <textarea data-testid="wiz-avail" value={data.availability} onChange={(e)=>setData(d=>({...d, availability:e.target.value}))}
                rows={3} className="w-full mt-1 mb-4 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 outline-none focus:border-[#FF6B6B] text-sm" placeholder="Ex : Remise à Gare de Lyon entre 8h et 10h" />
              <div className="text-xs text-slate-400 mb-2">Marchandises exclues</div>
              <div className="flex flex-wrap gap-2">
                {EXCLUSIONS_OPTS.map((o) => (
                  <button key={o} type="button" data-testid={`wiz-excl-${o}`} onClick={()=>toggleExcl(o)}
                    className={`px-3 py-1.5 rounded-full text-xs border ${data.exclusions.includes(o) ? "border-[#FF6B6B] bg-[#FF6B6B]/10 text-[#FF6B6B]" : "border-slate-700 bg-slate-900/40 text-slate-300"}`}>
                    {o}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 mt-5 text-sm">
                <input data-testid="wiz-hide" type="checkbox" checked={data.hide_contact} onChange={(e)=>setData(d=>({...d, hide_contact:e.target.checked}))} /> Masquer mes coordonnées (chat interne obligatoire)
              </label>
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input data-testid="wiz-pro" type="checkbox" checked={data.is_pro} onChange={(e)=>setData(d=>({...d, is_pro:e.target.checked}))} /> Trajet TravelBuddy Pro (fret)
              </label>
              {data.is_pro && (
                <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-800 pt-4">
                  <div><label className="text-[11px] text-slate-400">Poids max (kg)</label>
                    <input data-testid="wiz-fret-weight" type="number" value={data.freight?.max_weight_kg||""} onChange={(e)=>setData(d=>({...d, freight:{...d.freight, max_weight_kg: parseFloat(e.target.value)||null}}))} className="w-full mt-1 px-2 py-1.5 rounded bg-slate-900/60 border border-slate-700 text-sm"/></div>
                  <div><label className="text-[11px] text-slate-400">Volume max (m³)</label>
                    <input data-testid="wiz-fret-vol" type="number" step="0.1" value={data.freight?.max_volume_m3||""} onChange={(e)=>setData(d=>({...d, freight:{...d.freight, max_volume_m3: parseFloat(e.target.value)||null}}))} className="w-full mt-1 px-2 py-1.5 rounded bg-slate-900/60 border border-slate-700 text-sm"/></div>
                  <div className="col-span-2"><label className="text-[11px] text-slate-400">Véhicule</label>
                    <input data-testid="wiz-fret-vehicle" value={data.freight?.vehicle_type||""} onChange={(e)=>setData(d=>({...d, freight:{...d.freight, vehicle_type:e.target.value}}))} placeholder="Utilitaire / Semi / Frigo…" className="w-full mt-1 px-2 py-1.5 rounded bg-slate-900/60 border border-slate-700 text-sm"/></div>
                  <label className="col-span-2 flex items-center gap-2 text-xs"><input data-testid="wiz-fret-adr" type="checkbox" checked={data.freight?.adr_capable||false} onChange={(e)=>setData(d=>({...d, freight:{...d.freight, adr_capable:e.target.checked}}))}/> ADR (matières dangereuses)</label>
                  <label className="col-span-2 flex items-center gap-2 text-xs"><input data-testid="wiz-fret-temp" type="checkbox" checked={data.freight?.temperature_controlled||false} onChange={(e)=>setData(d=>({...d, freight:{...d.freight, temperature_controlled:e.target.checked}}))}/> Température dirigée</label>
                  {data.freight?.temperature_controlled && (
                    <>
                      <div><label className="text-[11px] text-slate-400">T° min (°C)</label><input data-testid="wiz-fret-tmin" type="number" value={data.freight?.temp_min_c||""} onChange={(e)=>setData(d=>({...d, freight:{...d.freight, temp_min_c: parseFloat(e.target.value)||null}}))} className="w-full mt-1 px-2 py-1.5 rounded bg-slate-900/60 border border-slate-700 text-sm"/></div>
                      <div><label className="text-[11px] text-slate-400">T° max (°C)</label><input data-testid="wiz-fret-tmax" type="number" value={data.freight?.temp_max_c||""} onChange={(e)=>setData(d=>({...d, freight:{...d.freight, temp_max_c: parseFloat(e.target.value)||null}}))} className="w-full mt-1 px-2 py-1.5 rounded bg-slate-900/60 border border-slate-700 text-sm"/></div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {step === 6 && (
            <div>
              <h2 className="font-display text-2xl font-bold mb-4">Aperçu</h2>
              <dl className="text-sm space-y-2">
                <Row k="Mode" v={data.mode} />
                <Row k="Départ" v={data.origin.label} />
                <Row k="Arrivée" v={data.destination.label} />
                <Row k="Heure départ" v={new Date(data.depart_at).toLocaleString("fr-FR")} />
                <Row k="Heure arrivée" v={new Date(data.arrive_at).toLocaleString("fr-FR")} />
                <Row k="Disponibilités" v={data.availability || "—"} />
                <Row k="Exclusions" v={data.exclusions.join(", ") || "Aucune"} />
                <Row k="Pro" v={data.is_pro ? "Oui" : "Non"} />
              </dl>
              <p className="text-[11px] text-slate-500 mt-4">Le trajet ne pourra pas être modifié après publication.</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between mt-6">
        <button data-testid="wiz-prev" disabled={step===1} onClick={()=>setStep(s=>s-1)} className="btn-ghost-glass px-4 py-2 rounded-lg text-sm disabled:opacity-40 flex items-center gap-1">
          <ChevronLeft size={14}/> Précédent
        </button>
        {step < 6 ? (
          <button data-testid="wiz-next" disabled={!canNext()} onClick={()=>setStep(s=>s+1)} className="btn-coral px-5 py-2 rounded-lg text-sm disabled:opacity-40 flex items-center gap-1">
            Suivant <ChevronRight size={14}/>
          </button>
        ) : (
          <button data-testid="wiz-publish" disabled={submitting} onClick={submit} className="btn-coral px-5 py-2 rounded-lg text-sm disabled:opacity-40 flex items-center gap-1">
            <Check size={14}/> {submitting ? "…" : "Publier le trajet"}
          </button>
        )}
      </div>
    </div>
  );
}

function Row({k,v}) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-800/60">
      <dt className="text-slate-400">{k}</dt><dd className="text-right text-slate-100">{v}</dd>
    </div>
  );
}
